/**
 * Grade espacial uniforme no plano XZ para consultas de vizinhança (criaturas, jogadores,
 * recursos, construções), no servidor e no cliente (ADR 0006). Cada id ocupa uma célula
 * pelo seu ponto; consultas por raio filtram pela distância real.
 */
const OFFSET = 32768;
const MIN_CELL = -OFFSET;
const MAX_CELL = OFFSET - 1;

function cellKey(cx: number, cz: number): number {
  return (cx + OFFSET) * 65536 + (cz + OFFSET);
}

interface Entry {
  x: number;
  z: number;
  key: number;
}

export class SpatialHash<Id = number> {
  readonly cellSize: number;
  private readonly cells = new Map<number, Set<Id>>();
  private readonly entries = new Map<Id, Entry>();

  constructor(cellSize: number) {
    if (!(cellSize > 0) || !Number.isFinite(cellSize)) throw new RangeError("cellSize deve ser finito e positivo");
    this.cellSize = cellSize;
  }

  get size(): number {
    return this.entries.size;
  }

  private cellOf(v: number): number {
    if (!Number.isFinite(v)) throw new RangeError(`coordenada não finita: ${v}`);
    const c = Math.floor(v / this.cellSize);
    if (c < MIN_CELL || c > MAX_CELL) throw new RangeError(`coordenada fora da grade: ${v}`);
    return c;
  }

  /** Célula de um limite de consulta, recortada à grade (consulta perto da borda não falha). */
  private clampedCell(v: number): number {
    const c = Math.floor(v / this.cellSize);
    return c < MIN_CELL ? MIN_CELL : c > MAX_CELL ? MAX_CELL : c;
  }

  /** Insere ou move o id para (x, z). */
  set(id: Id, x: number, z: number): void {
    const key = cellKey(this.cellOf(x), this.cellOf(z));
    const current = this.entries.get(id);
    if (current) {
      current.x = x;
      current.z = z;
      if (current.key === key) return;
      this.removeFromCell(id, current.key);
      current.key = key;
    } else {
      this.entries.set(id, { x, z, key });
    }
    let cell = this.cells.get(key);
    if (!cell) {
      cell = new Set();
      this.cells.set(key, cell);
    }
    cell.add(id);
  }

  delete(id: Id): boolean {
    const entry = this.entries.get(id);
    if (!entry) return false;
    this.removeFromCell(id, entry.key);
    this.entries.delete(id);
    return true;
  }

  has(id: Id): boolean {
    return this.entries.has(id);
  }

  /** Ids a no máximo `radius` de (x, z), acumulados em `out` (reutilizável para evitar alocação). */
  queryRadius(x: number, z: number, radius: number, out: Id[] = []): Id[] {
    if (!Number.isFinite(x) || !Number.isFinite(z) || !Number.isFinite(radius) || radius < 0) {
      throw new RangeError("queryRadius exige coordenadas finitas e raio finito >= 0");
    }
    const r2 = radius * radius;
    const minX = this.clampedCell(x - radius);
    const maxX = this.clampedCell(x + radius);
    const minZ = this.clampedCell(z - radius);
    const maxZ = this.clampedCell(z + radius);
    for (let cx = minX; cx <= maxX; cx++) {
      for (let cz = minZ; cz <= maxZ; cz++) {
        const cell = this.cells.get(cellKey(cx, cz));
        if (!cell) continue;
        for (const id of cell) {
          const e = this.entries.get(id) as Entry;
          const dx = e.x - x;
          const dz = e.z - z;
          if (dx * dx + dz * dz <= r2) out.push(id);
        }
      }
    }
    return out;
  }

  /**
   * Como `queryRadius`, mas escreve em `out[count…]` por índice e devolve a nova contagem: `out` nunca
   * encolhe (zerar o `length` de um array solta o buffer dele), então a busca a cada quadro não aloca.
   */
  queryRadiusInto(x: number, z: number, radius: number, out: Id[], count = 0): number {
    if (!Number.isFinite(x) || !Number.isFinite(z) || !Number.isFinite(radius) || radius < 0) {
      throw new RangeError("queryRadius exige coordenadas finitas e raio finito >= 0");
    }
    const r2 = radius * radius;
    const minX = this.clampedCell(x - radius);
    const maxX = this.clampedCell(x + radius);
    const minZ = this.clampedCell(z - radius);
    const maxZ = this.clampedCell(z + radius);
    let n = count;
    for (let cx = minX; cx <= maxX; cx++) {
      for (let cz = minZ; cz <= maxZ; cz++) {
        const cell = this.cells.get(cellKey(cx, cz));
        if (!cell) continue;
        for (const id of cell) {
          const e = this.entries.get(id) as Entry;
          const dx = e.x - x;
          const dz = e.z - z;
          if (dx * dx + dz * dz <= r2) out[n++] = id;
        }
      }
    }
    return n;
  }

  /** O id mais próximo dentro do raio que satisfaz `accept`, ou `undefined`. */
  nearest(x: number, z: number, radius: number, accept: (id: Id) => boolean = () => true): Id | undefined {
    let best: Id | undefined;
    let bestD = Infinity;
    for (const id of this.queryRadius(x, z, radius)) {
      if (!accept(id)) continue;
      const e = this.entries.get(id) as Entry;
      const d = (e.x - x) * (e.x - x) + (e.z - z) * (e.z - z);
      if (d < bestD) {
        bestD = d;
        best = id;
      }
    }
    return best;
  }

  clear(): void {
    this.cells.clear();
    this.entries.clear();
  }

  private removeFromCell(id: Id, key: number): void {
    const cell = this.cells.get(key);
    if (!cell) return;
    cell.delete(id);
    if (cell.size === 0) this.cells.delete(key);
  }
}
